import {
    Autocomplete,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useFormikContext } from "formik";
import React from "react";
import { useIntl } from "react-intl";

import { OPTIONS_COUNTRIES, localizeCountryOption } from "../../../lib/i18n/countryOptions";
import { initialValue } from "../../Form/helpers";
import { QuestionProps } from "../interfaces";

/** Input component for questions where the responder must select one out of several options. */
const SingleSelectQuestion: React.FC<QuestionProps> = ({ question, saveDraft, name, ...props }) => {
    const { values, getFieldMeta, errors, setFieldValue } = useFormikContext<Record<string, initialValue>>();
    const { formatMessage, locale } = useIntl();

    const optionLabel = (option: string) =>
        question.optionsTranslationKey === OPTIONS_COUNTRIES ? localizeCountryOption(option, locale) : option;
    const error = errors[question.id];
    const { touched } = getFieldMeta(`${question.id}.value`);
    const formValue = values[question.id];

    if (!question.options || question.options.length < 1 || !formValue) {
        return null;
    }

    const showError = touched && Boolean(error);
    const currentValue = formValue.value;
    const autocompleteValue = typeof currentValue === "string" ? currentValue : null;

    return (
        <FormControl sx={{ width: "100%" }} error={showError}>
            {question.options.length > 5 ? (
                <Autocomplete
                    options={question.options}
                    value={autocompleteValue}
                    getOptionLabel={optionLabel}
                    onChange={(_, newValue) => {
                        setFieldValue(question.id, {
                            value: newValue || "",
                            fileNames: formValue.fileNames,
                        });
                        saveDraft([newValue || ""]);
                    }}
                    disabled={props.disabled}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            error={showError}
                            placeholder={formatMessage({
                                defaultMessage: "Select one option",
                            })}
                            // HelperText is handled by FormHelperText below for consistency
                        />
                    )}
                />
            ) : (
                <RadioGroup
                    {...props}
                    sx={{ gap: 1 }}
                    value={formValue.value}
                    name={name}
                    onChange={(event) => {
                        setFieldValue(question.id, {
                            value: event.target.value,
                            fileNames: formValue.fileNames,
                        });
                        saveDraft([event.target.value]);
                    }}
                >
                    {question.options.map((option) => (
                        <FormControlLabel
                            value={option}
                            key={option}
                            control={<Radio disabled={props.disabled} />}
                            label={optionLabel(option)}
                        />
                    ))}
                </RadioGroup>
            )}
            <FormHelperText>
                <Stack direction="row" justifyContent="flex-start">
                    <Typography>{showError && error ? error.value : ""}</Typography>
                </Stack>
            </FormHelperText>
        </FormControl>
    );
};

export default SingleSelectQuestion;
